import { queryClient, queryKeys } from '@/lib/query-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { teamsService } from '../api/teamsService';
import type { AddTeamMemberInput, CreateTeamInput, UpdateTeamInput } from '../types/team';

export function useTeams() {
  return useQuery({
    queryKey: queryKeys.teams.lists(),
    queryFn: () => teamsService.listTeams(),
  });
}

export function useTeam(id: number | null) {
  return useQuery({
    queryKey: queryKeys.teams.detail(String(id)),
    queryFn: () => teamsService.getTeam(id!),
    enabled: id !== null,
  });
}

export function useCreateTeam() {
  return useMutation({
    mutationFn: (data: CreateTeamInput) => teamsService.createTeam(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
    meta: {
      resourceName: 'Team',
      action: 'create',
      errorTitle: 'Failed to Create Team',
    },
  });
}

export function useUpdateTeam(id: number) {
  return useMutation({
    mutationFn: (data: UpdateTeamInput) => teamsService.updateTeam(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(String(id)) });
    },
    meta: {
      resourceName: 'Team',
      action: 'update',
      errorTitle: 'Failed to Update Team',
    },
  });
}

export function useDeleteTeam() {
  return useMutation({
    mutationFn: (id: number) => teamsService.deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
    meta: {
      resourceName: 'Team',
      action: 'delete',
      errorTitle: 'Failed to Delete Team',
    },
  });
}

export function useAddTeamMember(teamId: number) {
  return useMutation({
    mutationFn: (data: AddTeamMemberInput) => teamsService.addTeamMember(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(String(teamId)) });
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
    meta: {
      // Members are added one mutation per user (see TeamDetailPage), so the global
      // handler would fire a toast per member. The Add Members modal shows a single
      // aggregate success/error toast instead, so suppress both here.
      skipSuccessToast: true,
      skipErrorToast: true,
    },
  });
}

export function useRemoveTeamMember(teamId: number) {
  return useMutation({
    mutationFn: ({ userId, newTeamLeadId }: { userId: number; newTeamLeadId?: number }) =>
      teamsService.removeTeamMember(teamId, userId, newTeamLeadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(String(teamId)) });
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
    meta: {
      successTitle: 'Member Removed',
      successMessage: 'Team member removed successfully',
      errorTitle: 'Failed to Remove Member',
    },
  });
}
